
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, Clock, Send, Instagram, Youtube } from "lucide-react";
import { useResponsive } from "../hooks/useResponsive";
import InteractiveCard from "./InteractiveCard";
import { colors } from "../styles/colors";
import { PrimaryButton } from './ui/StandardButton';
import HeroHeading from "./ui/HeroHeading";
import SeoAlternateLinks from "./seo/SeoAlternateLinks";
import { buildAbsoluteUrl, derivePageMeta } from "../utils/seo";
import LazyVideo from "./LazyVideo";
import { trackMetaEvent } from "../hooks/useMetaPixel";
import { useContent } from "../cms/ContentContext";
import { sparHandelse, sparLead } from "../utils/spar";

const COUNTRY_CODES = [
  { value: "+46", label: "Sweden (+46)" },
  { value: "+47", label: "Norway (+47)" },
  { value: "+45", label: "Denmark (+45)" },
  { value: "+358", label: "Finland (+358)" },
  { value: "+49", label: "Germany (+49)" },
  { value: "+48", label: "Poland (+48)" },
  { value: "+44", label: "United Kingdom (+44)" },
  { value: "+31", label: "Netherlands (+31)" },
  { value: "+33", label: "France (+33)" },
  { value: "+34", label: "Spain (+34)" },
  { value: "+39", label: "Italy (+39)" },
  { value: "+41", label: "Switzerland (+41)" },
  { value: "+43", label: "Austria (+43)" },
  { value: "+32", label: "Belgium (+32)" },
  { value: "+1", label: "USA / Canada (+1)" }
];





const Contact = () => {
  const { t, i18n } = useTranslation();
  // ...existing code...
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();
  const meta = derivePageMeta({
    t,
    i18n,
    pageKey: 'contact',
    routeKey: 'contact',
    defaults: {
      ogImage: '/og/contact.jpg',
    },
  });
  const ogImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.ogImage);
  const twitterImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.twitterImage);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneCountryCode: '+46',
    phone: '',
    subject: '',
    message: '',
    contactByPhone: false,
    consent: false
  });
  const [formStatus, setFormStatus] = useState({
    state: 'idle',
    message: ''
  });
  const apiBaseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  // Removed unused showClothesModal and setShowClothesModal

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormStatus({ state: 'idle', message: '' });
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim().replace(/\+/g, "");
    const trimmedSubject = formData.subject.trim();
    const trimmedMessage = formData.message.trim();
    const normalizedPhone = trimmedPhone
      ? `${formData.phoneCountryCode} ${trimmedPhone}`.trim()
      : '';

    // Utan kryss i rutan skickas ingenting vidare. Samtycket ar grunden for att
    // fa spara gasten i CRM:et, och det far aldrig antas.
    if (formData.consent !== true) {
      setFormStatus({
        state: 'error',
        message: t('contact.form.consentError', 'You need to tick the box before we can send this.')
      });
      return;
    }

    setFormStatus({
      state: 'loading',
      message: t('contact.form.sending', 'Sending your message...')
    });

    // Gasten gar till SCC forst och oberoende av sajtens egen backend. Gar den
    // ner ska forfragan anda ligga i Gustavs CRM.
    sparLead({
      name: trimmedName,
      email: trimmedEmail,
      phone: normalizedPhone,
      message: trimmedMessage,
      language: i18n?.language || 'en',
      consent_given: true,
      form: {
        subject: trimmedSubject || null,
        message: trimmedMessage || null,
        contact_by_phone: Boolean(formData.contactByPhone),
        language: i18n?.language || 'en'
      }
    });

    try {
      if (!apiBaseUrl) {
        throw new Error("Backend URL saknas");
      }

      const backendPayload = {
        name: trimmedName,
        email: trimmedEmail,
        subject: trimmedSubject || "No subject",
        message: trimmedMessage,
        phone: normalizedPhone,
        contactByPhone: Boolean(formData.contactByPhone)
      };

      const backendResponse = await fetch(`${apiBaseUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(backendPayload)
      });

      if (!backendResponse.ok) {
        const details = await backendResponse.text();
        throw new Error(details || "Backend contact submission failed");
      }

      setFormStatus({
        state: 'success',
        message: t('contact.form.successMessage', 'Thank you! We will get back to you shortly.')
      });
      sparHandelse("form_submit", { formular: "kontakt" });

      // Track Contact event in Meta Pixel
      trackMetaEvent("Contact", {
        content_name: trimmedSubject || "General Inquiry",
        content_category: "Contact Form"
      });

      setFormData({
        name: '',
        email: '',
        phoneCountryCode: '+46',
        phone: '',
        subject: '',
        message: '',
        contactByPhone: false,
        consent: false
      });
    } catch (err) {
      console.error('Contact form submission failed', err);
      setFormStatus({
        state: 'error',
        message: t('contact.form.errorMessage', 'Something went wrong. Please try again later or email us directly.')
      });
    }
  };

  const contactIsSubmitting = formStatus.state === 'loading';

  // Normalize contact details across locales
  const locale = i18n?.language || 'en';
  const normalizedLocationLine2 = locale === 'sv'
    ? 'Råstrand, Sverige'
    : (locale === 'de' ? 'Råstrand, Schweden' : 'Råstrand, Sweden');
  const phoneNoteNormalized = locale === 'sv'
    ? t('contact.info.phone.note')
    : (locale === 'de' ? t('contact.info.phone.note') : 'Available 9am–6pm CET');
  const primaryPhone = '+46 73 181 45 68';

  const contactInfo = [
    {
      icon: <MapPin className="h-6 w-6" />,
      title: t('contact.info.location.title'),
      details: [t('contact.info.location.line1'), normalizedLocationLine2],
      color: "text-green-500"
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: t('contact.info.phone.title'),
      details: [primaryPhone, phoneNoteNormalized],
      color: "text-blue-500"
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: t('contact.info.email.title'),
      details: [t('contact.info.email.address'), t('contact.info.email.note')],
      color: "text-cyan-500"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: t('contact.info.season.title'),
      details: [t('contact.info.season.dates'), t('contact.info.season.cta')],
      color: "text-orange-500"
    }
  ];

  const heroVideo = getMedia('contact.hero.videoSrc', '/optimized_videos/coldexperience1-opt.mp4');
  const heroPoster = getMedia('contact.hero.poster', '/images/Nya_bilder/IMG_0451.webp');
  const heroSpacing = isMobile ? 'px-4 pt-24 pb-16' : 'px-4 sm:px-6 lg:px-8 pt-32 pb-24';
  const heroMaxWidth = isMobile ? 'max-w-sm' : 'max-w-7xl';

  return (
    <>
      <Helmet>
        {meta.title && <title>{meta.title}</title>}
        {meta.description && <meta name="description" content={meta.description} />}
        {meta.keywords && <meta name="keywords" content={meta.keywords} />}
        <link rel="canonical" href={meta.canonicalUrl} />
        <meta property="og:title" content={meta.ogTitle} />
        <meta property="og:description" content={meta.ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={meta.canonicalUrl} />
        <meta name="twitter:image" content={twitterImageUrl} />
        {meta.jsonLd && (
          <script type="application/ld+json">{JSON.stringify(meta.jsonLd)}</script>
        )}
      </Helmet>
      <SeoAlternateLinks
        routeKey="contact"
        canonicalPath={meta.canonicalPath}
        siteUrl={meta.siteUrl}
      />
      <div className="min-h-screen" style={{ background: colors.gradients.heroSolid }}>
        {/* Hero Section */}
        <section
          data-hero="true"
          data-cms-section="contact:hero"
          data-cms-page="contact"
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
        >
          <div className="absolute inset-0">
            <LazyVideo
              src={heroVideo}
              poster={heroPoster}
              autoPlay
              muted
              loop
              playsInline
              preload={isMobile ? "none" : "metadata"}
              className="absolute inset-0"
              videoClassName="absolute inset-0 w-full h-full object-cover"
              pauseWhenOutOfView
            />
            <div className="absolute inset-0" style={{ background: colors.gradients.hero }}></div>
          </div>
          <div className={`relative z-10 w-full ${heroSpacing}`}>
            <div className={`${heroMaxWidth} mx-auto text-center`}>
              <HeroHeading
                primary={t('contact.hero.title1')}
                accent={t('contact.hero.title2')}
                className={`mb-6 ${isMobile ? 'text-4xl' : 'text-5xl md:text-6xl'
                  }`}
              />
              <p className={`mx-auto ${isMobile ? 'text-lg max-w-md' : 'text-xl max-w-3xl'
                }`} style={{ color: colors.text.secondary }}>
                {t('contact.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>
        {/* Contact Form & Info */}
        <section data-cms-section="contact" data-cms-page="contact" className={isMobile ? 'py-12' : 'py-20'}>
          <div className={`mx-auto ${isMobile ? 'max-w-sm px-4' : 'max-w-7xl px-4 sm:px-6 lg:px-8'
            }`}>
            <div className={`grid gap-12 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'
              }`}>
              {/* Contact Form */}
              <div className={isMobile ? '' : 'lg:col-span-2'}>
                <InteractiveCard
                  variant="glass"
                  className={`overflow-hidden ${isMobile ? 'p-6' : 'p-8'
                    }`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className={`rounded-full flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                        }`}
                      style={{ backgroundColor: colors.accent.primary + '20' }}
                    >
                      <Send className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'
                        }`} style={{ color: colors.accent.primary }} />
                    </div>
                    <h2 className={`font-bold ${isMobile ? 'text-2xl' : 'text-3xl'
                      }`} style={{ color: colors.text.primary }}>
                      {t('contact.form.title')}
                    </h2>
                  </div>

                  <p className={`mb-8 ${isMobile ? 'text-sm' : 'text-base'
                    }`} style={{ color: colors.text.primary }}>
                    {t('contact.form.subtitle', 'Vi svarar inom 24 timmar och hjälper dig planera din perfekta Lapplands-upplevelse.')}
                  </p>
                  <form
                    name="kontakt"
                    method="POST"
                    className="space-y-6"
                    onSubmit={handleSubmit}
                  >
                    <input type="hidden" name="success" value="Tack för din förfrågan!" />
                    {formStatus.state !== 'idle' && (
                      <div
                        className={`rounded-lg border px-4 py-3 text-sm ${formStatus.state === 'success'
                          ? 'border-green-400 bg-green-500/10 text-green-200'
                          : formStatus.state === 'error'
                            ? 'border-red-400 bg-red-500/10 text-red-200'
                            : 'border-blue-400 bg-blue-500/10 text-blue-100'
                          }`}
                        aria-live="polite"
                      >
                        {formStatus.message}
                      </div>
                    )}
                    <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
                      }`}>
                      <div>
                        <label htmlFor="contact-name" className={`block font-medium mb-2 ${isMobile ? 'text-sm' : 'text-sm'
                          }`} style={{ color: colors.text.primary }}>
                          {t('contact.form.nameLabel')}
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderColor: formData.name ? colors.accent.primary : 'rgba(255, 255, 255, 0.3)',
                            color: colors.primary,
                            '--tw-ring-color': colors.accent.primary + '20'
                          }}
                          placeholder={t('contact.form.namePlaceholder')}
                          required
                          autoComplete="name"
                          disabled={contactIsSubmitting}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className={`block font-medium mb-2 ${isMobile ? 'text-sm' : 'text-sm'
                          }`} style={{ color: colors.text.primary }}>
                          {t('contact.form.emailLabel')}
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderColor: formData.email ? colors.accent.primary : 'rgba(255, 255, 255, 0.3)',
                            color: colors.primary,
                            '--tw-ring-color': colors.accent.primary + '20'
                          }}
                          placeholder={t('contact.form.emailPlaceholder')}
                          required
                          autoComplete="email"
                          disabled={contactIsSubmitting}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className={`block font-medium mb-2 ${isMobile ? 'text-sm' : 'text-sm'
                        }`} style={{ color: colors.text.primary }}>
                        {t('contact.form.phoneLabel')}
                      </label>
                      <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-[180px_minmax(0,1fr)]'}`}>
                        <select
                          aria-label={t('contact.form.phoneCountryCodeLabel', 'Country code')}
                          name="phoneCountryCode"
                          value={formData.phoneCountryCode}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderColor: formData.phone ? colors.accent.primary : 'rgba(255, 255, 255, 0.3)',
                            color: colors.primary,
                            '--tw-ring-color': colors.accent.primary + '20'
                          }}
                          disabled={contactIsSubmitting}
                        >
                          {COUNTRY_CODES.map((country) => (
                            <option key={country.value} value={country.value}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                        <input
                          id="contact-phone"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={(event) => {
                            const sanitizedValue = event.target.value.replace(/\+/g, "");
                            setFormStatus({ state: 'idle', message: '' });
                            setFormData((prev) => ({
                              ...prev,
                              phone: sanitizedValue
                            }));
                          }}
                          className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderColor: formData.phone ? colors.accent.primary : 'rgba(255, 255, 255, 0.3)',
                            color: colors.primary,
                            '--tw-ring-color': colors.accent.primary + '20'
                          }}
                          placeholder={t('contact.form.phonePlaceholder')}
                          autoComplete="tel"
                          disabled={contactIsSubmitting}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className={`block font-medium mb-2 ${isMobile ? 'text-sm' : 'text-sm'
                        }`} style={{ color: colors.text.primary }}>
                        {t('contact.form.subjectLabel')}
                      </label>
                      <select
                        id="contact-subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderColor: formData.subject ? colors.accent.primary : 'rgba(255, 255, 255, 0.3)',
                          color: colors.primary,
                          '--tw-ring-color': colors.accent.primary + '20'
                        }}
                        required
                        disabled={contactIsSubmitting}
                      >
                        <option value="">{t('contact.form.subjectOptions.select')}</option>
                        <option value="booking">{t('contact.form.subjectOptions.booking')}</option>
                        <option value="7-day">{t('contact.form.subjectOptions.sevenDay')}</option>
                        <option value="5-day">{t('contact.form.subjectOptions.fiveDay')}</option>
                        <option value="1-day">{t('contact.form.subjectOptions.oneDay')}</option>
                        <option value="custom">{t('contact.form.subjectOptions.custom')}</option>
                        <option value="general">{t('contact.form.subjectOptions.general')}</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className={`block font-medium mb-2 ${isMobile ? 'text-sm' : 'text-sm'
                        }`} style={{ color: colors.text.primary }}>
                        {t('contact.form.messageLabel')}
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 resize-none"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderColor: formData.message ? colors.accent.primary : 'rgba(255, 255, 255, 0.3)',
                          color: colors.primary,
                          '--tw-ring-color': colors.accent.primary + '20'
                        }}
                        placeholder={t('contact.form.messagePlaceholder')}
                        required
                        autoComplete="off"
                        disabled={contactIsSubmitting}
                      ></textarea>
                    </div>

                    <InteractiveCard variant="glass" className="p-4">
                      <div className="flex items-center">
                        <input
                          id="contact-by-phone"
                          type="checkbox"
                          disabled={contactIsSubmitting}
                          name="contactByPhone"
                          checked={formData.contactByPhone}
                          onChange={handleInputChange}
                          className="h-4 w-4 border-gray-300 rounded"
                          style={{
                            accentColor: colors.accent.primary
                          }}
                        />
                        <label htmlFor="contact-by-phone" className={`ml-3 ${isMobile ? 'text-sm' : 'text-sm'
                          }`} style={{ color: colors.text.primary }}>
                          {t('contact.form.contactByPhone')}
                        </label>
                      </div>
                    </InteractiveCard>

                    <InteractiveCard variant="glass" className="p-4">
                      <div className="flex items-center">
                        <input
                          id="contact-consent"
                          type="checkbox"
                          disabled={contactIsSubmitting}
                          name="consent"
                          checked={formData.consent}
                          onChange={handleInputChange}
                          className="h-4 w-4 border-gray-300 rounded"
                          style={{
                            accentColor: colors.accent.primary
                          }}
                        />
                        <label htmlFor="contact-consent" className="ml-3 text-sm" style={{ color: colors.text.primary }}>
                          {t('contact.form.consentLabel')}
                        </label>
                      </div>
                    </InteractiveCard>

                    <PrimaryButton
                      type="submit"
                      className="w-full flex items-center justify-center"
                      size="large"
                      loading={contactIsSubmitting}
                      disabled={contactIsSubmitting}
                    >
                      {t('contact.form.sendButton')}
                      <Send className="ml-2 h-5 w-5" />
                    </PrimaryButton>
                  </form>
                </InteractiveCard>
              </div>
              {/* Contact Information */}
              <div className="space-y-6">
                {/* Quick Contact */}
                <InteractiveCard variant="glass" className={isMobile ? 'p-4' : 'p-6'}>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`rounded-full flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                        }`}
                      style={{ backgroundColor: colors.accent.primary + '20' }}
                    >
                      <MapPin className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'
                        }`} style={{ color: colors.accent.primary }} />
                    </div>
                    <h3 className={`font-semibold ${isMobile ? 'text-lg' : 'text-xl'
                      }`} style={{ color: colors.text.primary }}>
                      {t('contact.findusTitle')}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <p className={isMobile ? 'text-sm' : ''} style={{ color: colors.text.primary }}>
                      Råstrand 171<br />924 93 Gargnäs<br />Sweden
                    </p>
                    <a
                      href="https://maps.google.com/?q=65.2459661,17.9967012"
                      target="_blank"
                      rel="noreferrer"
                      className={`inline-flex items-center gap-2 font-medium hover:underline transition-colors ${isMobile ? 'text-sm' : ''
                        }`}
                      style={{ color: colors.accent.primary }}
                    >
                      <MapPin className="h-4 w-4" />
                      {t('contact.openMaps')}
                    </a>
                    <div className={`flex flex-col gap-1 ${isMobile ? 'text-sm' : ''
                      }`}>
                      <a
                        href="tel:+46731814568"
                        className="flex items-center gap-2 font-medium hover:underline transition-colors"
                        style={{ color: colors.accent.primary }}
                      >
                        <Phone className="h-4 w-4" />
                        +46 73 181 45 68
                      </a>
                      <a
                        href="tel:+46702229765"
                        className="flex items-center gap-2 font-medium hover:underline transition-colors"
                        style={{ color: colors.accent.primary }}
                      >
                        <Phone className="h-4 w-4" />
                        +46 70 222 97 65
                      </a>
                    </div>
                  </div>
                </InteractiveCard>

                {contactInfo.map((info, index) => (
                  <InteractiveCard key={index} variant="glass" className={isMobile ? 'p-4' : 'p-6'}>
                    <div className="flex items-start space-x-4">
                      <div
                        className={`rounded-lg flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                          }`}
                        style={{ backgroundColor: colors.accent.primary + '20' }}
                      >
                        <div style={{ color: colors.accent.primary }}>
                          {info.icon}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold mb-2 ${isMobile ? 'text-lg' : 'text-xl'
                          }`} style={{ color: colors.text.primary }}>
                          {info.title}
                        </h3>
                        <div className="space-y-1">
                          {info.title === t('contact.info.location.title') ? (
                            <a
                              href="https://maps.google.com/?q=Råstrand,Sweden"
                              target="_blank"
                              rel="noreferrer"
                              className={`font-medium ${isMobile ? 'text-sm' : 'text-sm'} hover:underline`}
                              style={{ color: colors.text.primary }}
                            >
                              {info.details[0]}
                            </a>
                          ) : info.title === t('contact.info.email.title') ? (
                            <a
                              href="mailto:info@coldexperience.se"
                              className={`font-medium ${isMobile ? 'text-sm' : 'text-sm'} hover:underline`}
                              style={{ color: colors.text.primary }}
                            >
                              {info.details[0]}
                            </a>
                          ) : (
                            <p className={`font-medium ${isMobile ? 'text-sm' : 'text-sm'}`} style={{ color: colors.text.primary }}>
                              {info.details[0]}
                            </p>
                          )}
                          {info.details[1] && (
                            <p className={`${isMobile ? 'text-sm' : 'text-sm'}`} style={{ color: colors.text.secondary }}>
                              {info.details[1]}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </InteractiveCard>
                ))}

                {/* Social Links */}
                <InteractiveCard variant="glass" className={isMobile ? 'p-4' : 'p-6'}>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`rounded-full flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                        }`}
                      style={{ backgroundColor: colors.accent.primary + '20' }}
                    >
                      <Instagram className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'
                        }`} style={{ color: colors.accent.primary }} />
                    </div>
                    <h3 className={`font-semibold ${isMobile ? 'text-lg' : 'text-xl'
                      }`} style={{ color: colors.text.primary }}>
                      {t('contact.social.title', 'Följ oss')}
                    </h3>
                  </div>
                  <p className={`mb-4 ${isMobile ? 'text-sm' : ''
                    }`} style={{ color: colors.text.primary }}>
                    {t('contact.social.subtitle', 'Se våra senaste äventyr och inspiration')}
                  </p>
                  <div className={`flex gap-3 ${isMobile ? 'flex-col' : 'flex-row'
                    }`}>
                    <a
                      href="https://www.instagram.com/coldexperience.se/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${isMobile ? 'justify-center' : ''
                        }`}
                      style={{
                        backgroundColor: colors.accent.primary + '10',
                        color: colors.accent.primary
                      }}
                    >
                      <Instagram className="h-5 w-5" />
                      <span className={isMobile ? 'text-sm' : 'text-sm'}>@coldexperience.se</span>
                    </a>
                    <a
                      href="https://www.youtube.com/@coldexperience"
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${isMobile ? 'justify-center' : ''
                        }`}
                      style={{
                        backgroundColor: colors.accent.primary + '10',
                        color: colors.accent.primary
                      }}
                    >
                      <Youtube className="h-5 w-5" />
                      <span className={isMobile ? 'text-sm' : 'text-sm'}>Cold Experience</span>
                    </a>
                  </div>
                </InteractiveCard>
              </div>
            </div>
          </div>
        </section>
        {/* Footer removed, now rendered globally in App.js */}
      </div>
    </>
  );
};

export default Contact;
